import {Info} from 'lucide-react'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Button} from '@/components/ui/button'
import type {AttributeDefinition} from '@/types/attribute-definition'
import {AttributeValueType} from '@/types/attribute'
import {useI18n} from '@/i18n'

interface HintRow {
    label: string
    value: string
    isPattern?: boolean
}

interface PatternVocab {
    uppercaseLetter: string
    lowercaseLetter: string
    digit: string
    wordChar: string
    space: string
    letter: string
    letterOrDigit: string
    uppercaseOrDigit: string
    letterDigitUnderscore: string
    letterDigitUnderscoreDash: string
    uppercaseDigitUnderscore: string
    uppercaseDigitUnderscoreDash: string
    anyChar: string
    oneOrMore: string
    zeroOrMore: string
    optional: string
}

function charClassLabel(cls: string, vocab: PatternVocab): string {
    const map: Record<string, string> = {
        '[A-Z]': vocab.uppercaseLetter,
        '[a-z]': vocab.lowercaseLetter,
        '[0-9]': vocab.digit,
        '[A-Za-z]': vocab.letter,
        '[A-Z0-9]': vocab.uppercaseOrDigit,
        '[A-Za-z0-9]': vocab.letterOrDigit,
        '[A-Za-z0-9_]': vocab.letterDigitUnderscore,
        '[A-Za-z0-9_-]': vocab.letterDigitUnderscoreDash,
        '[A-Z0-9_]': vocab.uppercaseDigitUnderscore,
        '[A-Z0-9_-]': vocab.uppercaseDigitUnderscoreDash,
    }
    return map[cls] ?? cls
}

function readQuantifier(str: string, i: number, vocab: PatternVocab): {label: string; advance: number} {
    if (i >= str.length) return {label: '', advance: 0}
    if (str[i] === '+') return {label: vocab.oneOrMore, advance: 1}
    if (str[i] === '*') return {label: vocab.zeroOrMore, advance: 1}
    if (str[i] === '?') return {label: vocab.optional, advance: 1}
    if (str[i] === '{') {
        const j = str.indexOf('}', i)
        if (j !== -1) {
            const inner = str.slice(i + 1, j)
            const adv = j - i + 1
            const parts = inner.split(',')
            if (parts.length === 1) return {label: `×${inner}`, advance: adv}
            if (!parts[1]) return {label: `${parts[0]}+`, advance: adv}
            return {label: `${parts[0]}–${parts[1]}`, advance: adv}
        }
    }
    return {label: '', advance: 0}
}

function describePattern(pattern: string, vocab: PatternVocab): string {
    const core = pattern.replace(/^\^/, '').replace(/\$$/, '')
    const tokens: string[] = []
    let i = 0
    while (i < core.length) {
        const litMatch = core.slice(i).match(/^([^[\\+?*{.()|]+)/)
        if (litMatch) {
            tokens.push(`"${litMatch[1]}"`)
            i += litMatch[1].length
            continue
        }
        if (core[i] === '[') {
            const closeIdx = core.indexOf(']', i + 1)
            if (closeIdx === -1) { i++; continue }
            const cls = core.slice(i, closeIdx + 1)
            i = closeIdx + 1
            const {label, advance} = readQuantifier(core, i, vocab)
            i += advance
            tokens.push(charClassLabel(cls, vocab) + (label ? ` (${label})` : ''))
            continue
        }
        if (core[i] === '\\' && i + 1 < core.length) {
            const esc = core[i + 1]
            i += 2
            const base = esc === 'd' ? vocab.digit : esc === 'w' ? vocab.wordChar : esc === 's' ? vocab.space : `\\${esc}`
            const {label, advance} = readQuantifier(core, i, vocab)
            i += advance
            tokens.push(base + (label ? ` (${label})` : ''))
            continue
        }
        if (core[i] === '.') {
            i++
            const {label, advance} = readQuantifier(core, i, vocab)
            i += advance
            tokens.push(vocab.anyChar + (label ? ` (${label})` : ''))
            continue
        }
        i++
    }
    return tokens.join(', ')
}

export function hasHintContent(def: AttributeDefinition): boolean {
    const rules = def.validationRules
    if (!rules) return false
    const vt = def.valueType
    if (vt === AttributeValueType.STRING || vt === AttributeValueType.REFERENCE) {
        if (rules.minLength != null || rules.maxLength != null || rules.pattern != null) return true
    }
    if (vt === AttributeValueType.NUMBER && (rules.min != null || rules.max != null)) return true
    if (rules.maxOccurrences != null && rules.maxOccurrences > 1) return true
    return false
}

export function AttributeHint({definition}: {definition: AttributeDefinition}) {
    const i18n = useI18n()
    const rules = definition.validationRules
    if (!rules) return null

    const av = i18n.attributeValidation
    const rows: HintRow[] = []
    const vt = definition.valueType

    const vocab: PatternVocab = {
        uppercaseLetter: av.patternUppercaseLetter,
        lowercaseLetter: av.patternLowercaseLetter,
        digit: av.patternDigit,
        wordChar: av.patternWordChar,
        space: av.patternSpace,
        letter: av.patternLetter,
        letterOrDigit: av.patternLetterOrDigit,
        uppercaseOrDigit: av.patternUppercaseOrDigit,
        letterDigitUnderscore: av.patternLetterDigitUnderscore,
        letterDigitUnderscoreDash: av.patternLetterDigitUnderscoreDash,
        uppercaseDigitUnderscore: av.patternUppercaseDigitUnderscore,
        uppercaseDigitUnderscoreDash: av.patternUppercaseDigitUnderscoreDash,
        anyChar: av.patternAnyChar,
        oneOrMore: av.patternOneOrMore,
        zeroOrMore: av.patternZeroOrMore,
        optional: av.patternOptional,
    }

    if (vt === AttributeValueType.STRING || vt === AttributeValueType.REFERENCE) {
        if (rules.minLength != null && rules.maxLength != null) {
            const val = rules.minLength === rules.maxLength
                ? av.hintCharExact({n: rules.minLength})
                : av.hintCharRange({min: rules.minLength, max: rules.maxLength})
            rows.push({label: av.hintLabelLength, value: val})
        } else if (rules.minLength != null) {
            rows.push({label: av.hintLabelLength, value: av.hintCharMin({min: rules.minLength})})
        } else if (rules.maxLength != null) {
            rows.push({label: av.hintLabelLength, value: av.hintCharMax({max: rules.maxLength})})
        }
        if (rules.pattern) {
            rows.push({label: av.hintLabelPattern, value: rules.pattern, isPattern: true})
        }
    }

    if (vt === AttributeValueType.NUMBER) {
        if (rules.min != null && rules.max != null) {
            rows.push({label: av.hintLabelRange, value: av.hintRange({min: rules.min, max: rules.max})})
        } else if (rules.min != null) {
            rows.push({label: av.hintLabelRange, value: av.hintMin({min: rules.min})})
        } else if (rules.max != null) {
            rows.push({label: av.hintLabelRange, value: av.hintMax({max: rules.max})})
        }
    }

    if (rules.maxOccurrences != null && rules.maxOccurrences > 1) {
        rows.push({label: av.hintLabelMaxValues, value: String(rules.maxOccurrences)})
    }

    if (rows.length === 0) return null

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 text-muted-foreground hover:text-foreground shrink-0"
                    data-testid={`hint-${definition.name}`}
                >
                    <Info className="h-3 w-3"/>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3 space-y-2.5 text-sm" side="bottom" align="start">
                {rows.map((row, idx) => (
                    <div key={idx}>
                        <p className="text-xs font-medium text-muted-foreground mb-0.5">{row.label}</p>
                        {row.isPattern ? (
                            <div className="space-y-1">
                                <p className="text-xs text-foreground">{describePattern(row.value, vocab)}</p>
                                <code className="block text-xs bg-muted rounded px-2 py-1 font-mono break-all">{row.value}</code>
                            </div>
                        ) : (
                            <p className="text-foreground">{row.value}</p>
                        )}
                    </div>
                ))}
            </PopoverContent>
        </Popover>
    )
}
